import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useProductFullQuery } from "@/modules/catalog/presentation/api/queries";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useCompareByVariantQuery } from "../api/queries";
import { PricingToolbar } from "../ui/PricingToolbar";

function formatLak(n: number): string {
  return n.toLocaleString("en-US");
}

export function ComparePriceByBranchPage() {
  const nav = useNavigate({ from: "/app/pricing/compare" });
  const search = useSearch({ from: "/app/pricing/compare" });
  const [productId, setProductId] = useState<string | null>(null);
  const [variantId, setVariantId] = useState<string | null>(null);

  const productFullQuery = useProductFullQuery(productId ?? "");
  const compareQuery = useCompareByVariantQuery(variantId ?? "");

  const product = productFullQuery.data ?? null;
  const variants = product?.variants ?? [];
  const compare = compareQuery.data ?? null;

  const variantOptions = useMemo(
    () => [
      { value: "none", label: "ເລືອກ variant..." },
      ...variants.map((v) => ({
        value: v.id,
        label: `${v.name} (${formatLak(v.basePriceLak)} LAK)`,
      })),
    ],
    [variants],
  );

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ປຽບທຽບລາຄາຕໍ່ສາຂາ",
              to: "/app/pricing/compare",
            },
          ]}
        />
      </Header>
      <Main>
        <PricingToolbar
          title="ປຽບທຽບລາຄາຕໍ່ສາຂາ"
          description="ເບິ່ງລາຄາ variant ໜຶ່ງແບບຕາມສາຂາ (base, override, ລາຄາໃຊ້ຈິງ)."
        >
          {compare && (
            <Button
              variant="outline"
              onClick={() =>
                nav({
                  to: "/app/pricing/branch-overrides",
                  search: { ...search, variantId: "none" },
                })
              }
            >
              ແກ້ລາຄາຕໍ່ສາຂາ
            </Button>
          )}
        </PricingToolbar>

        <div className="mb-4 flex flex-wrap items-center gap-4">
          <div className="flex min-w-[160px] items-center gap-2">
            {/* <span className="whitespace-nowrap text-muted-foreground text-sm">
              ສິນຄ້າ:
            </span> */}
            <InfiniteCombobox<{ id: string; name: string }>
              queryKey={["catalog", "products", "lookup"]}
              queryFn={(args) =>
                fetchLookupForInfinite(
                  `${config.apiUrl}/catalog/products/lookup`,
                  args,
                )
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem(
                  `${config.apiUrl}/catalog/products/lookup`,
                  id,
                )
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              value={productId ?? ""}
              onValueChange={(v) => {
                setProductId(v || null);
                setVariantId(null);
              }}
              placeholder="ເລືອກສິນຄ້າ..."
              className="w-full"
              clearable
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap text-muted-foreground text-sm">
              Variant:
            </span>
            <SimpleSelect
              value={variantId ?? "none"}
              onValueChange={(v) => setVariantId(v || null)}
              options={variantOptions}
              placeholder="ເລືອກ variant..."
              className="min-w-[220px]"
              disabled={!productId}
            />
          </div>
        </div>

        <div className="rounded-xl border bg-card">
          {!variantId ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
              <p>ເລືອກສິນຄ້າ ແລະ variant ເພື່ອເບິ່ງລາຄາແຕ່ລະສາຂາ</p>
            </div>
          ) : compareQuery.isLoading ? (
            <div className="py-16 text-center text-muted-foreground">
              ກຳລັງໂຫຼດ...
            </div>
          ) : !compare ? (
            <div className="py-16 text-center text-muted-foreground">
              ບໍ່ພົບຂໍ້ມູນ
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">ສາຂາ</TableHead>
                    <TableHead className="w-[120px] text-right">
                      ລາຄາ base (LAK)
                    </TableHead>
                    <TableHead className="w-[120px] text-right">
                      ລາຄາ override (LAK)
                    </TableHead>
                    <TableHead className="w-[120px] text-right">
                      ລາຄາໃຊ້ຈິງ (LAK)
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-muted/50">
                    <TableCell colSpan={4} className="font-medium">
                      {compare.variant.productName} →{" "}
                      {compare.variant.variantName}
                    </TableCell>
                  </TableRow>
                  {compare.branchPrices.map((row) => (
                    <TableRow key={row.branchId}>
                      <TableCell>{row.branchName}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatLak(compare.variant.basePriceLak)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.overridePriceLak != null
                          ? formatLak(row.overridePriceLak)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatLak(row.effectivePriceLak)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </Main>
    </>
  );
}
